/**
 * Logo theme variant utilities (plain JS port).
 *
 * Analyzes logo color contrast against light/dark theme backgrounds and
 * generates monotone variants / downscaled versions of uploaded logo files.
 */

const THEME_BG_LIGHT = "#FCFDFF";
const THEME_BG_DARK = "#0b1220";
const WCAG_THRESHOLD = 3.0;
const ANALYSIS_MAX_DIM = 128;

/**
 * Convert a hex color string to an { r, g, b } object.
 * @param {string} hex - Hex color, e.g. "#FCFDFF".
 * @returns {{ r: number, g: number, b: number }}
 */
function hexToRgb(hex) {
  const clean = hex.replace(/^#/, "");
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return { r, g, b };
}

/**
 * Convert an sRGB channel value (0-255) to linear RGB.
 * @param {number} c
 * @returns {number}
 */
function sRGBToLinear(c) {
  const v = c / 255;
  if (v <= 0.03928) return v / 12.92;
  return Math.pow((v + 0.055) / 1.055, 2.4);
}

/**
 * Compute the WCAG relative luminance of an RGB color.
 * @param {{ r: number, g: number, b: number }} param0
 * @returns {number}
 */
function relativeLuminance({ r, g, b }) {
  return (
    0.2126 * sRGBToLinear(r) +
    0.7152 * sRGBToLinear(g) +
    0.0722 * sRGBToLinear(b)
  );
}

/**
 * Compute the WCAG contrast ratio between two RGB colors.
 * @param {{ r: number, g: number, b: number }} a
 * @param {{ r: number, g: number, b: number }} b
 * @returns {number}
 */
function contrastRatio(a, b) {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Load an image file, preferring createImageBitmap and falling back to an
 * HTMLImageElement via an object URL.
 * @param {File} file
 * @returns {Promise<{ bitmap: ImageBitmap|null, image: HTMLImageElement|null, width: number, height: number, url: string|null }>}
 */
async function loadImage(file) {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file);
      if (bitmap.width > 0 && bitmap.height > 0) {
        return { bitmap, image: null, width: bitmap.width, height: bitmap.height, url: null };
      }
      bitmap.close?.();
    } catch {
      // fall through to HTMLImageElement
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    await new Promise((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("No se pudo cargar la imagen"));
      image.src = url;
    });
    return {
      bitmap: null,
      image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      url,
    };
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
}

/**
 * Draw a source image onto a fresh canvas of the given dimensions.
 * @param {CanvasImageSource} source
 * @param {number} width
 * @param {number} height
 * @returns {HTMLCanvasElement}
 */
function drawToCanvas(source, width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No se pudo obtener el contexto del canvas");
  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(source, 0, 0, width, height);
  return canvas;
}

/**
 * Export a canvas to a PNG File.
 * @param {HTMLCanvasElement} canvas
 * @param {string} filename
 * @returns {Promise<File>}
 */
function canvasToBlobFile(canvas, filename) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("No se pudo exportar la imagen"));
        return;
      }
      resolve(new File([blob], filename, { type: "image/png" }));
    }, "image/png");
  });
}

/**
 * Analyze a logo file's mean color and contrast against theme backgrounds.
 * @param {File} file
 * @returns {Promise<{ meanColor: { r: number, g: number, b: number }, contrastLight: number, contrastDark: number, conflictsLight: boolean, conflictsDark: boolean }>}
 */
export async function analyzeLogo(file) {
  const loaded = await loadImage(file);
  try {
    let srcW = loaded.width;
    let srcH = loaded.height;
    if (!srcW || !srcH) {
      srcW = ANALYSIS_MAX_DIM;
      srcH = ANALYSIS_MAX_DIM;
    }

    const scale = Math.min(1, ANALYSIS_MAX_DIM / Math.max(srcW, srcH));
    const drawW = Math.max(1, Math.round(srcW * scale));
    const drawH = Math.max(1, Math.round(srcH * scale));

    const source = loaded.bitmap ?? loaded.image;
    const canvas = drawToCanvas(source, drawW, drawH);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("No se pudo obtener el contexto del canvas");
    const { data } = ctx.getImageData(0, 0, drawW, drawH);

    let rSum = 0;
    let gSum = 0;
    let bSum = 0;
    let count = 0;

    for (let i = 0; i < data.length; i += 4) {
      const alpha = data[i + 3];
      if (alpha <= 128) continue;
      rSum += data[i];
      gSum += data[i + 1];
      bSum += data[i + 2];
      count++;
    }

    if (count === 0) {
      return {
        meanColor: { r: 0, g: 0, b: 0 },
        contrastLight: 0,
        contrastDark: 0,
        conflictsLight: true,
        conflictsDark: true,
      };
    }

    const meanColor = {
      r: Math.round(rSum / count),
      g: Math.round(gSum / count),
      b: Math.round(bSum / count),
    };

    const lightBg = hexToRgb(THEME_BG_LIGHT);
    const darkBg = hexToRgb(THEME_BG_DARK);
    const contrastLight = contrastRatio(meanColor, lightBg);
    const contrastDark = contrastRatio(meanColor, darkBg);

    return {
      meanColor,
      contrastLight,
      contrastDark,
      conflictsLight: contrastLight < WCAG_THRESHOLD,
      conflictsDark: contrastDark < WCAG_THRESHOLD,
    };
  } finally {
    if (loaded.bitmap) loaded.bitmap.close?.();
    if (loaded.url) URL.revokeObjectURL(loaded.url);
  }
}

/**
 * Generate a monotone (single-ink) variant of a logo, preserving alpha.
 * @param {File} file
 * @param {string} inkHex - Hex color to apply to non-transparent pixels.
 * @returns {Promise<File>}
 */
export async function generateMonotoneVariant(file, inkHex) {
  const loaded = await loadImage(file);
  try {
    let srcW = loaded.width;
    let srcH = loaded.height;
    if (!srcW || !srcH) {
      srcW = 256;
      srcH = 256;
    }

    const source = loaded.bitmap ?? loaded.image;
    const canvas = drawToCanvas(source, srcW, srcH);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("No se pudo obtener el contexto del canvas");
    const imageData = ctx.getImageData(0, 0, srcW, srcH);
    const data = imageData.data;
    const ink = hexToRgb(inkHex);

    for (let i = 0; i < data.length; i += 4) {
      const alpha = data[i + 3];
      if (alpha === 0) continue;
      data[i] = ink.r;
      data[i + 1] = ink.g;
      data[i + 2] = ink.b;
    }

    ctx.putImageData(imageData, 0, 0);
    return canvasToBlobFile(canvas, "logo-variant.png");
  } finally {
    if (loaded.bitmap) loaded.bitmap.close?.();
    if (loaded.url) URL.revokeObjectURL(loaded.url);
  }
}

/**
 * Downscale an image file so its longest side is at most maxDim.
 * Returns the original file unchanged if no downscaling is needed.
 * @param {File} file
 * @param {number} maxDim
 * @returns {Promise<File>}
 */
export async function downscaleImage(file, maxDim) {
  const loaded = await loadImage(file);
  try {
    const srcW = loaded.width;
    const srcH = loaded.height;
    if (!srcW || !srcH) return file;
    const longest = Math.max(srcW, srcH);
    if (longest <= maxDim) return file;

    const scale = maxDim / longest;
    const drawW = Math.max(1, Math.round(srcW * scale));
    const drawH = Math.max(1, Math.round(srcH * scale));

    const source = loaded.bitmap ?? loaded.image;
    const canvas = drawToCanvas(source, drawW, drawH);
    return canvasToBlobFile(canvas, file.name);
  } finally {
    if (loaded.bitmap) loaded.bitmap.close?.();
    if (loaded.url) URL.revokeObjectURL(loaded.url);
  }
}
